import { Button, Space, Tag, Tooltip } from 'antd';
import type { ERPColumnDef } from '@/components/DataTable';
import { formatDate } from '@/utils/format';
import type {
  StockLookupCategory,
  StockLookupComboRow,
  StockLookupLotRow,
  StockLookupProductRow,
  StockLookupSerialRow,
  ReservedOrderRow,
  WarehouseBreakdownRow,
} from '@/services/stockLookup.service';

/**
 * TCR-1954 — mỗi chế độ tra cứu có TRỤC DỮ LIỆU riêng nên có bộ cột riêng:
 * hàng hoá/combo gộp toàn kho (không có cột Kho), lô/mã quy cách là một dòng cho mỗi
 * (bản ghi × kho) nên bắt buộc có cột Kho. Đừng cố dùng chung một bộ cột.
 */

/** TCR-1984 — `type="link"` ăn token `colorLink` = colorPrimary (đỏ đô brand), mà đỏ là màu
 *  người dùng đọc thành "cảnh báo hết hàng". Ép xanh link chuẩn, cùng pattern TCR-1348/TCR-576. */
const LINK_COLOR = '#1677ff';

/**
 * TCR-1988 — ô "Loại hàng hoá": danh mục đầu tiên + tag "+N" (hover xem đủ).
 *
 * Một hàng hoá thuộc nhiều danh mục; trước đây chỉ hiện `category` (phần tử đầu) nên lọc
 * "Marvis" ra dòng ghi "Chăm sóc body" — QA đọc thành lọc sai. Đang lọc theo danh mục nào
 * thì đưa danh mục đó lên đầu để thấy ngay vì sao dòng khớp.
 */
export function categoryCell(
  row: { category: string; categories?: StockLookupCategory[] },
  preferredCategoryId?: string,
) {
  const categories = row.categories?.length
    ? [...row.categories].sort(
        (a, b) => Number(b.id === preferredCategoryId) - Number(a.id === preferredCategoryId),
      )
    : row.category
      ? [{ id: '', name: row.category }]
      : [];
  if (categories.length === 0) return null;
  const [first, ...rest] = categories;
  if (rest.length === 0) return <span>{first.name}</span>;
  return (
    <Space size={4} wrap>
      <span>{first.name}</span>
      <Tooltip title={categories.map((c) => c.name).join(', ')}>
        <Tag style={{ marginInlineEnd: 0, cursor: 'default' }} data-testid="category-more">
          +{rest.length}
        </Tag>
      </Tooltip>
    </Space>
  );
}

/** Số 0 render thành text thường: mở popup con cho một con số 0 chỉ ra bảng rỗng. */
function numberCell(value: number, onClick?: () => void) {
  if (!value || !onClick) return <span>{Number(value ?? 0).toLocaleString('vi-VN')}</span>;
  return (
    <Button type="link" style={{ padding: 0, color: LINK_COLOR }} onClick={onClick}>
      {value.toLocaleString('vi-VN')}
    </Button>
  );
}

/** TCR-2649 — số kho hiện sẵn trong ô; còn lại gom vào "…còn N kho" (bấm mở popup xem đủ). */
export const MAX_INLINE_WAREHOUSES = 5;

/**
 * TCR-2649 — ô "Số lượng": con số tổng (bấm mở popup tồn theo kho như cũ) + bên dưới từng
 * dòng `MÃ KHO: SL`. Chỉ hiện MÃ kho cho gọn; mã có thể trùng giữa các kho (KHO.QT) hoặc
 * ghi lệch vùng so với tên (KHOHCM014 = kho HN) nên rê chuột hiện "mã - tên (chi nhánh)".
 */
export function quantityByWarehouseCell(row: StockLookupProductRow, onClick: () => void) {
  const warehouses = row.warehouses ?? [];
  const shown = warehouses.slice(0, MAX_INLINE_WAREHOUSES);
  const hidden = warehouses.length - shown.length;
  return (
    <div>
      {numberCell(row.unit_in_stock, onClick)}
      {shown.map((w) => (
        <div
          key={w.warehouse_id}
          data-testid="stock-by-warehouse-line"
          style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', whiteSpace: 'nowrap', lineHeight: '18px' }}
        >
          <Tooltip
            title={`${w.warehouse_code || '(không mã)'} - ${w.warehouse_name}${w.store_name ? ` (${w.store_name})` : ''}`}
          >
            <span style={{ cursor: 'help' }}>{w.warehouse_code || w.warehouse_name}</span>
          </Tooltip>
          : {w.quantity.toLocaleString('vi-VN')}
        </div>
      ))}
      {hidden > 0 && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto', fontSize: 12, color: LINK_COLOR }}
          onClick={onClick}
        >
          …còn {hidden} kho
        </Button>
      )}
    </div>
  );
}

/**
 * TCR-2523 — so sánh hai số nullable để sort: dòng CHƯA có số (null/undefined) nằm
 * CUỐI, không nằm đầu như giá trị 0 — nếu không, "chưa có tồn AMIS" sẽ chen vào giữa
 * các dòng có tồn thật và đọc như "hết hàng".
 */
export function compareNullableNumbers(
  a: number | null | undefined,
  b: number | null | undefined,
): number {
  const av = a === null || a === undefined ? null : Number(a);
  const bv = b === null || b === undefined ? null : Number(b);
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av - bv;
}

/**
 * TCR-2523 — ô cột "Tồn AMIS".
 *
 * - BE chưa trả (`undefined`) hoặc trả `null` → hiện "—". TUYỆT ĐỐI không hiện 0:
 *   0 trông như "hết hàng" trong khi thực chất là "chưa có dữ liệu".
 * - Lệch so với tồn ERP → TÔ NHẸ (Tag màu cảnh báo) + tooltip nói rõ độ lệch
 *   (`ERP - AMIS`). Người dùng tự quyết định tin bên nào, FE không tự ý chỉnh
 *   số của ERP.
 */
export function amisQuantityCell(
  value: number | null | undefined,
  erpQuantity: number | null | undefined,
) {
  if (value === null || value === undefined) {
    return <span data-testid="amis-quantity-empty">—</span>;
  }
  const amount = Number(value);
  const formatted = amount.toLocaleString('vi-VN');
  const erp = Number(erpQuantity ?? 0);
  if (amount === erp) {
    return <span data-testid="amis-quantity">{formatted}</span>;
  }
  const diff = erp - amount;
  return (
    <Tooltip title={`Lệch so với ERP: ${diff.toLocaleString('vi-VN')}`}>
      <Tag color="warning" style={{ marginInlineEnd: 0 }} data-testid="amis-quantity-diff">
        {formatted}
      </Tag>
    </Tooltip>
  );
}

export function buildProductColumns({
  onQuantityClick,
  onReservedClick,
  preferredCategoryId,
}: {
  onQuantityClick: (row: StockLookupProductRow) => void;
  onReservedClick: (row: StockLookupProductRow) => void;
  preferredCategoryId?: string;
}): ERPColumnDef<StockLookupProductRow>[] {
  return [
    { key: 'sku', title: 'Mã hàng hoá', dataIndex: 'sku', width: 180, alwaysVisible: true },
    { key: 'product_name', title: 'Tên hàng hoá', dataIndex: 'product_name', alwaysVisible: true },
    {
      key: 'category',
      title: 'Loại hàng hoá',
      dataIndex: 'category',
      width: 160,
      hideable: true,
      render: (_: string, row) => categoryCell(row, preferredCategoryId),
    },
    { key: 'uom', title: 'Đơn vị tính', dataIndex: 'uom', width: 110, hideable: true },
    {
      key: 'unit_in_stock',
      title: 'Số lượng',
      dataIndex: 'unit_in_stock',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.unit_in_stock - b.unit_in_stock,
      render: (_: number, row) => quantityByWarehouseCell(row, () => onQuantityClick(row)),
    },
    {
      // TCR-2523 — cột Tồn AMIS đứng NGAY CẠNH cột Số lượng (cùng nhóm "tồn kho") để
      // người dùng đối chiếu hai con số mà không phải nhảy qua nhảy lại.
      key: 'amis_quantity',
      title: 'Tồn AMIS',
      dataIndex: 'amis_quantity',
      width: 150,
      align: 'right',
      hideable: true,
      sorter: (a, b) => compareNullableNumbers(a.amis_quantity, b.amis_quantity),
      // Cột "Số lượng" (tồn ERP) GIỮ NGUYÊN cách hiện — lệch AMIS chỉ được báo ở
      // cột này, không được làm đổi/thay thế số ERP.
      render: (value: number | null | undefined, row) =>
        amisQuantityCell(value, row.unit_in_stock),
    },
    {
      key: 'unit_reserved',
      title: 'SL đã đặt chưa giao',
      dataIndex: 'unit_reserved',
      width: 180,
      align: 'right',
      sorter: (a, b) => a.unit_reserved - b.unit_reserved,
      render: (value: number, row) => numberCell(value, () => onReservedClick(row)),
    },
    {
      key: 'available_quantity',
      title: 'SL có thể đặt',
      dataIndex: 'available_quantity',
      width: 150,
      align: 'right',
      sorter: (a, b) => a.available_quantity - b.available_quantity,
      // Hiển thị thẳng field của read-model, KHÔNG lấy hiệu `unit_in_stock - unit_reserved`
      // ở FE: read-model đã kẹp sàn 0 và xử lý tồn theo trạng thái vị trí kho.
      render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
    },
  ];
}

export const lotColumns: ERPColumnDef<StockLookupLotRow>[] = [
  { key: 'warehouse_name', title: 'Kho', dataIndex: 'warehouse_name', width: 180, alwaysVisible: true },
  { key: 'sku', title: 'Mã hàng hoá', dataIndex: 'sku', width: 180 },
  { key: 'product_name', title: 'Tên hàng hoá', dataIndex: 'product_name' },
  { key: 'category', title: 'Loại hàng hoá', dataIndex: 'category', width: 140, hideable: true },
  { key: 'uom', title: 'Đơn vị tính', dataIndex: 'uom', width: 110, hideable: true },
  { key: 'lot_number', title: 'Số lô', dataIndex: 'lot_number', width: 140 },
  {
    key: 'expiry_date',
    title: 'Hạn sử dụng',
    dataIndex: 'expiry_date',
    width: 140,
    render: (value: string | null) => (value ? formatDate(value) : '-'),
  },
  {
    key: 'quantity',
    title: 'Số lượng tồn',
    dataIndex: 'quantity',
    width: 140,
    align: 'right',
    sorter: (a, b) => a.quantity - b.quantity,
    render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
  },
];

export const serialColumns: ERPColumnDef<StockLookupSerialRow>[] = [
  { key: 'warehouse_name', title: 'Kho', dataIndex: 'warehouse_name', width: 180, alwaysVisible: true },
  { key: 'sku', title: 'Mã hàng hoá', dataIndex: 'sku', width: 180 },
  { key: 'product_name', title: 'Tên hàng hoá', dataIndex: 'product_name' },
  { key: 'category', title: 'Loại hàng hoá', dataIndex: 'category', width: 140, hideable: true },
  { key: 'uom', title: 'Đơn vị tính', dataIndex: 'uom', width: 110, hideable: true },
  // Bản tham chiếu MISA có 5 cột "Mã quy cách 1..5" vì bên đó một dòng tồn mang nhiều mã.
  // Hệ này mỗi serial là một bản ghi `stock_serials` riêng → một cột là đủ và đúng.
  { key: 'serial_number', title: 'Mã quy cách', dataIndex: 'serial_number', width: 180 },
  {
    key: 'quantity',
    title: 'Số lượng tồn',
    dataIndex: 'quantity',
    width: 140,
    align: 'right',
    render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
  },
];

export function buildComboColumns(
  preferredCategoryId?: string,
): ERPColumnDef<StockLookupComboRow>[] {
  return [
    { key: 'sku', title: 'Mã hàng hoá', dataIndex: 'sku', width: 200, alwaysVisible: true },
    { key: 'product_name', title: 'Tên hàng hoá', dataIndex: 'product_name' },
    {
      key: 'category',
      title: 'Loại hàng hoá',
      dataIndex: 'category',
      width: 160,
      render: (_: string, row) => categoryCell(row, preferredCategoryId),
    },
    { key: 'uom', title: 'Đơn vị tính', dataIndex: 'uom', width: 110 },
    {
      key: 'unit_in_stock',
      title: 'Số lượng tồn',
      dataIndex: 'unit_in_stock',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.unit_in_stock - b.unit_in_stock,
      render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
    },
  ];
}

export const comboColumns = buildComboColumns();

/** TCR-2573 — "Mã - Tên" như cột Chi nhánh ở màn Tồn kho; BE cũ chưa trả thì để trống. */
export function storeLabel(row: Pick<WarehouseBreakdownRow, 'store_code' | 'store_name'>): string {
  return [row.store_code, row.store_name].filter(Boolean).join(' - ');
}

export const warehouseBreakdownColumns: ERPColumnDef<WarehouseBreakdownRow>[] = [
  // TCR-2573 — tên kho trùng nhau giữa các chi nhánh (vd hai dòng "KHO SỈ ONLINE HN"),
  // thiếu cột này thì không biết dòng nào của chi nhánh nào.
  {
    key: 'store_name',
    title: 'Chi nhánh',
    dataIndex: 'store_name',
    render: (_value: string, row) => storeLabel(row) || '—',
  },
  {
    // TCR-2649 — "Mã kho - Tên kho", cùng mã hiện ở cột Số lượng của bảng tra cứu.
    key: 'warehouse_name',
    title: 'Kho',
    dataIndex: 'warehouse_name',
    render: (_value: string, row) =>
      [row.warehouse_code, row.warehouse_name].filter(Boolean).join(' - ') || '—',
  },
  {
    key: 'quantity',
    title: 'Số lượng',
    dataIndex: 'quantity',
    width: 140,
    align: 'right',
    render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
  },
];

/** TCR-1990 — đơn sàn nằm ở `oc_orders`, mở ở `/ecom/orders/:id`; `/orders/:id` ra màn trắng. */
export function reservedOrderHref(row: ReservedOrderRow): string {
  return row.source_type === 'oc_order' ? `/ecom/orders/${row.order_id}` : `/orders/${row.order_id}`;
}

/** TCR-1997 — BE cũ chưa trả `can_view` → giữ hành vi cũ (mở được). */
export function canOpenReservedOrder(row: ReservedOrderRow): boolean {
  return row.can_view !== false;
}

const CHANNEL_LABELS: Record<string, string> = {
  TIKTOK: 'TikTok',
  SHOPEE: 'Shopee',
  LAZADA: 'Lazada',
};

/** Trạng thái đơn sàn đã KẾT THÚC mà phiếu giữ vẫn còn hiệu lực — số giữ đang bị treo. */
const ECOM_ENDED_STATES: Record<string, string> = {
  COMPLETED: 'Đã hoàn thành',
  CANCELLED: 'Đã huỷ',
};

export const reservedOrderColumns: ERPColumnDef<ReservedOrderRow>[] = [
  {
    key: 'order_number',
    title: 'Mã đơn',
    dataIndex: 'order_number',
    // TCR-2649 — bớt bề rộng các cột cố định để cột "Khách hàng" (không đặt width, ăn phần
    // còn lại) không bị bóp còn ~90px, tên công ty gãy 4–5 dòng.
    width: 170,
    // TCR-1989 — chữ đen trước đây không cho biết là bấm được. Link xanh (cùng LINK_COLOR);
    // `stopPropagation` để không mở HAI tab (dòng cũng có onClick mở đơn).
    render: (value: string, row) => (
      <Space size={4} wrap>
        {canOpenReservedOrder(row) ? (
          <a
            href={reservedOrderHref(row)}
            target="_blank"
            rel="noreferrer"
            style={{ color: LINK_COLOR }}
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ) : (
          // TCR-1997 — đơn của người khác: liệt kê để biết ai đang giữ hàng mà hỏi mượn,
          // nhưng không mở chi tiết (trang chi tiết cũng sẽ chặn theo phạm vi xem).
          <Tooltip title="Đơn của nhân viên khác — bạn không có quyền xem chi tiết">
            <span>{value}</span>
          </Tooltip>
        )}
        {row.source_type === 'oc_order' && (
          <Tag color="purple" style={{ marginInlineEnd: 0 }}>
            {CHANNEL_LABELS[row.channel ?? ''] ?? 'Đơn sàn'}
          </Tag>
        )}
        {row.source_type === 'oc_order' && row.state && ECOM_ENDED_STATES[row.state] && (
          <Tooltip title="Đơn sàn đã kết thúc nhưng phiếu giữ hàng vẫn còn hiệu lực">
            <Tag color="orange" style={{ marginInlineEnd: 0 }}>
              {ECOM_ENDED_STATES[row.state]}
            </Tag>
          </Tooltip>
        )}
      </Space>
    ),
  },
  {
    key: 'order_date',
    title: 'Ngày đặt',
    dataIndex: 'order_date',
    width: 110,
    render: (value: string) => (value ? formatDate(value) : '-'),
  },
  {
    // TCR-1997 — NV KD cần biết ai lên đơn để trao đổi mượn hàng.
    key: 'created_by_name',
    title: 'Người lên đơn',
    dataIndex: 'created_by_name',
    width: 220,
    render: (value: string | null | undefined, row) =>
      value ? (
        <Space size={4} wrap>
          <span>{value}</span>
          {row.created_by_staff_code && (
            <Tag style={{ marginInlineEnd: 0 }}>{row.created_by_staff_code}</Tag>
          )}
        </Space>
      ) : (
        '-'
      ),
  },
  { key: 'customer_name', title: 'Khách hàng', dataIndex: 'customer_name' },
  {
    key: 'quantity',
    title: 'SL giữ',
    dataIndex: 'quantity',
    width: 90,
    align: 'right',
    render: (value: number) => Number(value ?? 0).toLocaleString('vi-VN'),
  },
];
